import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Stack, Button, IconButton, Tooltip, TextField, InputAdornment, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions, Divider, Alert,
} from '@mui/material';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { PRIMARY } from '../theme';
import { card, ghostBtn, tonalBtn, field, EmptyState, SkeletonRows } from './uiKit';
import http, { apiErrorMessage } from '../Api/http';
import { GetEmployeeCredentials } from '../Api/Api';

// The response shape isn't documented beyond "a row per account", so accept a
// bare array or { items } / { accounts } under data.
const rowsOf = (data) => {
    if (Array.isArray(data)) return data;
    return data?.items || data?.accounts || data?.credentials || [];
};

function CredentialRow({ row }) {
    const [shown, setShown] = useState(false);
    const [copied, setCopied] = useState(false);

    const viewable = row.passwordViewable !== false;
    const password = row.password ?? '';

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(password);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch { /* clipboard blocked — the eye icon still lets them read it */ }
    };

    return (
        <Box sx={{ ...card, px: 1.6, py: 1.2, borderRadius: '9px', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ flex: '1 1 0', minWidth: 120 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#0F172A' }} noWrap>{row.name || row.employeeName || '—'}</Typography>
                <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }} noWrap>{row.loginId || '—'}</Typography>
            </Box>

            <Box sx={{ flex: '0 0 auto', minWidth: 0, ml: 'auto' }}>
                {viewable ? (
                    <Stack direction="row" sx={{ alignItems: 'center', gap: 0.3 }}>
                        <Typography sx={{ fontSize: 13, fontFamily: 'monospace', color: '#0F172A', letterSpacing: shown ? 0 : 1.5, mr: 0.6 }} noWrap>
                            {shown ? password : '••••••••'}
                        </Typography>
                        <Tooltip arrow title={shown ? 'Hide password' : 'Show password'}>
                            <IconButton size="small" onClick={() => setShown((v) => !v)} sx={{ color: '#94A3B8', '&:hover': { color: PRIMARY } }}>
                                {shown ? <VisibilityOffRoundedIcon sx={{ fontSize: 18 }} /> : <VisibilityRoundedIcon sx={{ fontSize: 18 }} />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip arrow title={copied ? 'Copied' : 'Copy password'}>
                            <IconButton size="small" onClick={copy} sx={{ color: copied ? '#16A34A' : '#94A3B8', '&:hover': { color: PRIMARY } }}>
                                {copied ? <CheckRoundedIcon sx={{ fontSize: 18 }} /> : <ContentCopyRoundedIcon sx={{ fontSize: 18 }} />}
                            </IconButton>
                        </Tooltip>
                    </Stack>
                ) : (
                    <Tooltip arrow title={row.note || 'Reset this password once to make it viewable.'}>
                        <Chip label="Reset needed" size="small" sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: '#FFF7ED', color: '#B45309' }} />
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
}

export default function CredentialsDialog({ open, onClose }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [query, setQuery] = useState('');

    useEffect(() => {
        const t = setTimeout(() => setQuery(search.trim()), 350);
        return () => clearTimeout(t);
    }, [search]);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const { data: body } = await http.get(GetEmployeeCredentials, { params: query ? { search: query } : undefined });
            if (body?.error) throw new Error(body.message || 'Could not load credentials.');
            setRows(rowsOf(body?.data));
        } catch (err) {
            // Zero rows comes back as a 404 on this API — treat it as an empty list.
            if (err?.response?.status === 404) setRows([]);
            else setError(err?.response || err?.request ? apiErrorMessage(err, 'Could not load credentials.') : err.message);
        } finally {
            setLoading(false);
        }
    }, [query]);

    // Fetch on open only — and drop the data again on close so plaintext
    // passwords don't linger in component state.
    useEffect(() => {
        if (open) load();
        else { setRows([]); setSearch(''); setQuery(''); setError(''); }
    }, [open, load]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth slotProps={{ paper: { sx: { borderRadius: '12px', width: 500, maxWidth: 'calc(100% - 32px)' } } }}>
            <DialogTitle sx={{ p: 2.5, pb: 1.5 }}>
                <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                    <Stack direction="row" spacing={1.4} sx={{ alignItems: 'center' }}>
                        <Box sx={{ width: 40, height: 40, borderRadius: '7px', bgcolor: '#F1EEFE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <KeyRoundedIcon sx={{ color: PRIMARY }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#0F172A' }}>Login credentials</Typography>
                            <Typography sx={{ fontSize: 12.5, color: '#98A0AE' }}>Login id and password for every account</Typography>
                        </Box>
                    </Stack>
                    <IconButton onClick={onClose} size="small"><CloseRoundedIcon /></IconButton>
                </Stack>
            </DialogTitle>
            <Divider />
            {/* Only the list scrolls (the warning and search stay put), so there is a single scrollbar. */}
            <DialogContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Alert severity="warning" sx={{ mb: 2, borderRadius: '7px', fontSize: 12.5 }}>
                    This list is sensitive. Don’t share or screenshot passwords.
                </Alert>

                <TextField
                    fullWidth size="small" placeholder="Search by login id or name" value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ ...field, mb: 1.5 }}
                    slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 18, color: '#94A3B8' }} /></InputAdornment> } }}
                />

                {loading && <SkeletonRows count={5} height={58} p={0} />}

                {!loading && error && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{error}</Typography>
                        <Button onClick={load} startIcon={<RefreshRoundedIcon />} sx={{ ...tonalBtn, mt: 1.5, height: 38, px: 2.2 }}>Try again</Button>
                    </Box>
                )}

                {!loading && !error && rows.length === 0 && (
                    <EmptyState icon={KeyRoundedIcon} title={query ? 'No accounts match your search' : 'No accounts found'} />
                )}

                {!loading && !error && rows.length > 0 && (
                    <Stack spacing={1} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
                        {rows.map((r, i) => <CredentialRow key={r.loginId || r.id || i} row={r} />)}
                    </Stack>
                )}
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} sx={{ ...ghostBtn, height: 40, px: 2.5 }}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
